/* eslint-disable */
// Place at: src/components/DeleteAccountModal.js
import {ActivityIndicator, Modal, Pressable, StyleSheet, Text, View} from 'react-native';
import Svg, {Circle, Path} from 'react-native-svg';
import {useTranslation} from 'react-i18next';
import {colors, fonts, radii, spacing, typography} from '../config/theme';

const Icon = ({children, color, size = 18, fill = 'none'}) => (
  <Svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={fill}
    stroke={color}
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round">
    {children}
  </Svg>
);

// triangle with exclamation mark
const AlertIcon = ({color, size}) => (
  <Icon color={color} size={size}>
    <Path d="M10.3 3.9L1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
    <Path d="M12 9v4M12 17h.01" />
  </Icon>
);

// trash can
const TrashIcon = ({color, size}) => (
  <Icon color={color} size={size}>
    <Path d="M3 6h18M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6M10 11v6M14 11v6" />
  </Icon>
);

// small "x" used in the consequences list
const CrossIcon = ({color, size}) => (
  <Icon color={color} size={size}>
    <Circle cx={12} cy={12} r={9} />
    <Path d="M9 9l6 6M15 9l-6 6" />
  </Icon>
);

// Confirmation sheet shown before the account is deleted.
//   visible, onClose
//   onConfirm: async () => void   (should throw on failure)
//   loading:   true while the server request is running
const DeleteAccountModal = ({visible, onClose, onConfirm, loading}) => {
  const {t} = useTranslation();

  const consequences = [
    t('root.DeleteAccount.item1', 'Your profile and personal details'),
    t('root.DeleteAccount.item2', 'Your scans, favourites and rewards'),
    t('root.DeleteAccount.item3', 'Your fuel and location preferences'),
  ];

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={() => !loading && onClose()}>
      <Pressable
        style={styles.backdrop}
        onPress={() => !loading && onClose()}
      />
      <View style={styles.center} pointerEvents="box-none">
        <View style={styles.card}>
          <View style={styles.iconCircle}>
            <AlertIcon color={colors.error} size={28} />
          </View>

          <Text style={styles.title}>
            {t('root.DeleteAccount.title', 'Delete your account?')}
          </Text>
          <Text style={styles.body}>
            {t(
              'root.DeleteAccount.body',
              'This is permanent and cannot be undone. The following will be deleted:',
            )}
          </Text>

          <View style={styles.list}>
            {consequences.map(text => (
              <View key={text} style={styles.listRow}>
                <CrossIcon color={colors.error} size={16} />
                <Text style={styles.listText}>{text}</Text>
              </View>
            ))}
          </View>

          <Text style={styles.note}>
            {t(
              'root.DeleteAccount.note',
              'You will be logged out and will not be able to log in with this account again.',
            )}
          </Text>

          <Pressable
            onPress={onConfirm}
            disabled={loading}
            accessibilityRole="button"
            style={({pressed}) => [
              styles.deleteBtn,
              pressed && styles.pressed,
              loading && styles.disabled,
            ]}>
            {loading ? (
              <ActivityIndicator color={colors.onPrimary} />
            ) : (
              <>
                <TrashIcon color={colors.onPrimary} size={18} />
                <Text style={styles.deleteText}>
                  {t('root.DeleteAccount.confirm', 'Yes, delete my account')}
                </Text>
              </>
            )}
          </Pressable>

          <Pressable
            onPress={onClose}
            disabled={loading}
            accessibilityRole="button"
            style={({pressed}) => [
              styles.cancelBtn,
              pressed && styles.pressed,
              loading && styles.disabled,
            ]}>
            <Text style={styles.cancelText}>
              {t('root.DeleteAccount.cancel', 'Cancel')}
            </Text>
          </Pressable>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0.5)',
  },
  center: {
    flex: 1,
    justifyContent: 'center',
    paddingHorizontal: spacing.margin,
  },
  card: {
    backgroundColor: colors.surface,
    borderRadius: radii.sheet,
    padding: spacing.lg,
    alignItems: 'center',
    gap: spacing.md,
  },
  iconCircle: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: colors.errorTint,
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    fontFamily: fonts.bold,
    fontSize: 20,
    lineHeight: 26,
    color: colors.textStrong,
    textAlign: 'center',
  },
  body: {
    ...typography.bodyMd,
    color: colors.textMuted,
    textAlign: 'center',
  },
  list: {
    alignSelf: 'stretch',
    gap: spacing.sm,
    backgroundColor: colors.errorTint,
    borderRadius: radii.card,
    padding: spacing.md,
  },
  listRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  listText: {
    ...typography.bodySm,
    color: colors.textStrong,
    flex: 1,
  },
  note: {
    ...typography.bodySm,
    color: colors.textMuted,
    textAlign: 'center',
  },
  deleteBtn: {
    alignSelf: 'stretch',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    height: 52,
    borderRadius: 999,
    backgroundColor: colors.error,
  },
  deleteText: {
    ...typography.labelMd,
    fontSize: 15,
    color: colors.onPrimary,
  },
  cancelBtn: {
    alignSelf: 'stretch',
    alignItems: 'center',
    justifyContent: 'center',
    height: 52,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.surface,
  },
  cancelText: {
    ...typography.labelMd,
    fontSize: 15,
    color: colors.textEmphasis,
  },
  pressed: {opacity: 0.85},
  disabled: {opacity: 0.6},
});

export default DeleteAccountModal;